/**
 * One floor in one room, reflecting.
 *
 * Almost everything that makes a polished floor look polished is already
 * happening without this: a low roughness, a broad environment gradient
 * sliding across it as you turn, and specular highlights that stretch. That is
 * what every device gets, it costs one cube map for the whole game, and it is
 * most of the effect.
 *
 * A planar reflector is the remaining fraction, and it is not free in the way
 * the rest is: it renders the scene a second time from a mirrored camera, so
 * its cost is a whole extra view of the world scaled by how many pixels you
 * give it. That buys one thing the environment map cannot fake — the room's
 * own architecture and the hostiles standing in it, upside down, moving
 * correctly. In a world built out of tall pale masses that is worth having in
 * the one place you look at longest, and worth having nowhere else.
 *
 * So: the largest room on the floor, on the tiers that can pay, at a fraction
 * of the resolution, and switched off entirely the moment you are not in it.
 * The brief's own rule for this, which is the right one: reflection should
 * enhance a composition, never rescue one.
 */

import * as THREE from 'three';
import { Reflector } from 'three/examples/jsm/objects/Reflector.js';
import type { Tier } from './quality.ts';
import type { Place } from './place.ts';

/**
 * How many pixels the second view gets.
 *
 * Deliberately small. A reflection in a floor with roughness a fifth is a
 * suggestion of the room, not a mirror of it — half the detail is thrown away
 * by the blur it is composited under, so rendering it sharp is rendering
 * something nobody will see.
 */
const RESOLUTION: Partial<Record<Tier, number>> = { balanced: 256, high: 512 };
/** Beyond this from the middle of the room, it is not your floor any more. */
const RANGE = 26;

export interface Reflections {
  /** Follow the player: off when they are elsewhere, which is most of the time. */
  update(eye: THREE.Vector3): void;
  dispose(): void;
}

/**
 * Put a reflector in the biggest room, if this machine is having one.
 *
 * Returns null on mobile and on any floor with no room worth it, and the
 * caller is expected to carry on exactly as before — nothing else in the look
 * depends on this existing.
 */
export function addReflections(
  group: THREE.Group,
  places: Place[],
  tier: Tier,
): Reflections | null {
  const res = RESOLUTION[tier];
  if (!res) return null;

  /*
   * A rectangle, or nothing.
   *
   * The reflector is one flat plane the size of the room's bounding box, and
   * a place that is L-shaped or wraps a corner has a bounding box that reaches
   * straight through its walls into the rooms next door — where it shows them
   * the mirrored ceiling of somewhere they cannot see. So a room only gets one
   * if it nearly fills the rectangle it is measured by.
   */
  const best = places
    .filter((p) => {
      const box = (p.max[0] - p.min[0]) * (p.max[1] - p.min[1]);
      return p.area > 90 && box > 0 && p.area / box > 0.82;
    })
    .sort((a, b) => b.area - a.area)[0];
  if (!best) return null;

  const w = best.max[0] - best.min[0];
  const d = best.max[1] - best.min[1];
  const cx = (best.min[0] + best.max[0]) / 2;
  const cy = (best.min[1] + best.max[1]) / 2;

  const mirror = new Reflector(new THREE.PlaneGeometry(w * 0.94, d * 0.94), {
    textureWidth: res,
    textureHeight: res,
    // Not silver. A reflector at full strength is a mirror, and a mirror floor
    // doubles every object in the room including the ones that were supposed
    // to be the only thing in it. This is a sheen over the resin underneath.
    color: 0x2f3134,
  });
  /*
   * A sheen, which means adding light rather than replacing it.
   *
   * `Reflector` draws an opaque plane: `color` times the reflected view, and
   * nothing of whatever is underneath. So "a sheen over the resin" was the
   * intent and not the behaviour — the floor material stopped mattering
   * anywhere the plane covered, and what the player saw was the reflection at
   * eighteen per cent, which on an open yard under a bright sky is a black
   * hole where the ground should be. A tank farm came out 71.8% dark with
   * white buildings standing in it and a `#50555b` slab underneath that was
   * never once drawn.
   *
   * Additive, no depth write: the floor draws first in the opaque pass and
   * this adds its highlight on top, which is what a polished surface does and
   * what the tint was always sized for.
   */
  const sheen = mirror.material as THREE.ShaderMaterial;
  sheen.transparent = true;
  sheen.blending = THREE.AdditiveBlending;
  sheen.depthWrite = false;
  /*
   * And mostly grey, because a rough surface does not return a colour.
   *
   * Adding the reflection back put the ground's value right and brought its
   * colour with it: a floor reflecting a room reflects every accent in the
   * room, doubles it, and spreads it over the largest surface in the frame.
   * `tinted` went from six of twenty-four maps to twelve, which is the style's
   * own rule — neutral surfaces, one accent spent thinly — being broken by the
   * floor rather than by anything anybody placed.
   *
   * A mirror returns colour; a polished floor at roughness a fifth returns a
   * blurred suggestion, and blur is most of the way to the mean. So a tenth of
   * the chroma survives and the luminance all of it, which is the half of the
   * reflection that was worth having.
   *
   * On the 24-map sample, counting gate failures rather than clean maps —
   * because the same fifteen maps keep failing one thing each and the clean
   * count sits still while the picture underneath it moves:
   *
   *                              clean  subject  tinted  masses  accent
   *   opaque, before any of this   5      14       6       5       2
   *   ceiling value fixed          9      11       6       5       2
   *   additive, full chroma        9       7      10       3       0
   *   additive, quarter chroma     9       7      10       3       0
   *   additive, tenth chroma       9       7       8       3       0
   *
   * A tank farm's worst view over that span: 18.8% pale, 71.8% dark, luminance
   * 0.243 — white buildings standing in a black hole — to 24.5%, 56.4%, 0.314,
   * with the buildings legible in the floor.
   */
  sheen.fragmentShader = sheen.fragmentShader.replace(
    'gl_FragColor = vec4( blendOverlay( base.rgb, color ), 1.0 );',
    [
      'vec3 refl = blendOverlay( base.rgb, color );',
      'float lum = dot( refl, vec3( 0.2126, 0.7152, 0.0722 ) );',
      'gl_FragColor = vec4( mix( vec3( lum ), refl, 0.10 ), 1.0 );',
    ].join('\n'),
  );
  sheen.needsUpdate = true;
  mirror.name = 'reflection';
  mirror.renderOrder = 1;
  mirror.rotation.x = -Math.PI / 2;
  // A finger above the floor, so it never fights the resin for the same
  // pixels; below it and the floor wins, level with it and they flicker.
  mirror.position.set(cx, best.floor + 0.012, -cy);
  group.add(mirror);

  const middle = new THREE.Vector3(cx, best.floor, -cy);
  return {
    update(eye: THREE.Vector3): void {
      /*
       * A reflector that is not visible still renders its view every frame
       * unless something says otherwise, which is the entire cost of the
       * feature being paid in rooms you are not standing in.
       *
       * Height as well as distance: a mezzanine floor seen from the storey
       * below is a plane you are looking at the back of, and what a reflector
       * shows through its own back is the mirrored ceiling of a room you are
       * not in.
       */
      const flat = Math.hypot(eye.x - middle.x, eye.z - middle.z);
      mirror.visible = flat < RANGE && eye.y > middle.y - 0.5 && eye.y < middle.y + 6;
    },
    dispose(): void {
      group.remove(mirror);
      mirror.geometry.dispose();
      (mirror.material as THREE.Material).dispose();
      mirror.dispose?.();
    },
  };
}
